<template>
    <DemoToolsLauncher
        v-if="hasActions && isEnabledForUser"
        ref="launcher"
        v-model:open="popoverOpen"
        title="Assessment demo tools"
        :scope="hubFallback ? 'assessment-hub' : 'page'">
        <div v-if="selectedAction === null" class="flex flex-col gap-4">
            <div>
                <h2 class="body-small-bold text-ink">Assessment demo tools</h2>
                <p v-if="cycleActions.length || responseActions.length || campaignActions.length" class="details text-ink-muted">Pick an assessment to work with.</p>
            </div>

            <div v-if="cycleActions.length" class="flex flex-col gap-2">
                <div class="scaffold-details-bold text-ink-muted">Cycles</div>
                <BaseButton
                    v-for="action in cycleActions"
                    :key="action.id"
                    theme="neutral"
                    size="sm"
                    class="assessment-row"
                    :aria-label="`Open demo tools for ${action.label}`"
                    @click="openAction('cycle', action)">
                    <i class="bi bi-stars shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ action.label }}</span>
                    <i class="bi bi-chevron-right shrink-0 text-ink-muted" aria-hidden="true"></i>
                </BaseButton>
            </div>

            <div v-if="responseActions.length" class="flex flex-col gap-2">
                <div class="scaffold-details-bold text-ink-muted">In progress</div>
                <BaseButton
                    v-for="action in responseActions"
                    :key="action.id"
                    theme="neutral"
                    size="sm"
                    class="assessment-row"
                    :aria-label="`Open demo tools for ${action.label}`"
                    @click="openAction('response', action)">
                    <i class="bi bi-pencil-square shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ action.label }}</span>
                    <i class="bi bi-chevron-right shrink-0 text-ink-muted" aria-hidden="true"></i>
                </BaseButton>
            </div>

            <div v-if="bootstrapKind" class="flex flex-col gap-2">
                <div class="scaffold-details-bold text-ink-muted">Create demo data</div>
                <p class="details text-ink-muted">Sets up a {{ bootstrapLabel }} in your name, fills it with synthetic responses and unlocks the report.</p>
                <BaseButton
                    theme="primary"
                    size="sm"
                    class="action"
                    :disabled="busyActionKey !== null"
                    :aria-label="`Create a completed ${bootstrapLabel}`"
                    @click="bootstrapAssessment">
                    <i class="bi bi-magic" aria-hidden="true"></i>
                    <span>{{ busyActionKey === "bootstrap" ? "Queuing…" : `Completed ${bootstrapLabel}` }}</span>
                </BaseButton>
                <p v-if="inlineError" class="details text-status-error" role="alert">{{ inlineError }}</p>
            </div>

            <div v-if="campaignActions.length" class="flex flex-col gap-2">
                <div class="scaffold-details-bold text-ink-muted">Campaigns</div>
                <BaseButton
                    v-for="action in campaignActions"
                    :key="action.id"
                    theme="neutral"
                    size="sm"
                    class="assessment-row"
                    :aria-label="`Open demo tools for ${action.label}`"
                    @click="openAction('campaign', action)">
                    <i class="bi bi-people shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ action.label }}</span>
                    <i class="bi bi-chevron-right shrink-0 text-ink-muted" aria-hidden="true"></i>
                </BaseButton>
            </div>
        </div>

        <div v-else class="flex flex-col gap-4">
            <div class="flex items-center gap-2">
                <BaseButton theme="neutral" size="sm" class="back" aria-label="Back to assessment list" @click="backToList">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i>
                </BaseButton>
                <div class="min-w-0">
                    <h2 class="body-small-bold truncate text-ink">{{ selectedAction.label }}</h2>
                    <p class="details text-ink-muted">{{ kindLabels[selectedAction.kind] }}</p>
                </div>
            </div>

            <div v-if="selectedAction.canFill !== false" class="flex flex-col gap-2 border-t border-border pt-4">
                <div class="scaffold-details-bold text-ink-muted">Synthetic data</div>

                <label v-if="selectedAction.kind === 'campaign'" for="synthetic-subject-count" class="flex flex-col gap-2">
                    <span class="details text-ink-muted">Number of participants</span>
                    <input
                        id="synthetic-subject-count"
                        v-model.number="subjectCount"
                        type="number"
                        min="1"
                        max="20"
                        step="1"
                        required
                        class="count-input"
                        aria-describedby="synthetic-subject-count-help" />
                    <span id="synthetic-subject-count-help" class="details text-ink-muted">Choose between 1 and 20. Repeated runs add more participants.</span>
                </label>
                <p v-else-if="selectedAction.kind === 'response'" class="details text-ink-muted">
                    Answers the questions you have left blank (every required one, and some of the optional ones), then jumps you to the last step. Submitting is still up to you.
                </p>
                <p v-else-if="selectedAction.kind === 'cycle'" class="details text-ink-muted">
                    Completes this assessment with synthetic responses and unlocks its report. Adds synthetic raters if none are pending; no invitations are sent.
                </p>

                <BaseButton
                    theme="primary"
                    size="sm"
                    class="action"
                    :disabled="busyActionKey !== null || !isSelectionValid"
                    :aria-label="actionAriaLabel"
                    @click="runSelectedAction">
                    <i class="bi bi-magic" aria-hidden="true"></i>
                    <span>{{ busyActionKey === null ? actionButtonText : busyButtonText }}</span>
                </BaseButton>
            </div>

            <div v-if="selectedAction.kind === 'cycle'" class="flex flex-col gap-2 border-t border-border pt-4">
                <div class="scaffold-details-bold text-ink-muted">Delete</div>
                <p class="details text-ink-muted">Removes this assessment along with its invites, responses and report. There is no undo.</p>

                <BaseButton
                    v-if="!confirmingDelete"
                    ref="deleteButton"
                    theme="danger"
                    size="sm"
                    class="action"
                    :disabled="busyActionKey !== null"
                    :aria-label="`Delete ${selectedAction.label}`"
                    @click="startConfirmingDelete">
                    <i class="bi bi-trash" aria-hidden="true"></i>
                    <span>Delete assessment</span>
                </BaseButton>
                <template v-else>
                    <BaseButton
                        ref="confirmDeleteButton"
                        theme="danger"
                        size="sm"
                        class="action"
                        :disabled="busyActionKey !== null"
                        :aria-label="`Confirm deleting ${selectedAction.label}`"
                        @click="deleteSelectedCycle">
                        <i class="bi bi-trash" aria-hidden="true"></i>
                        <span>{{ isDeleting ? "Deleting…" : "Yes, delete it" }}</span>
                    </BaseButton>
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        class="action"
                        :disabled="busyActionKey !== null"
                        :aria-label="`Cancel deleting ${selectedAction.label}`"
                        @click="cancelConfirmingDelete">
                        Cancel
                    </BaseButton>
                </template>
            </div>

            <p v-if="inlineError" class="details text-status-error" role="alert">{{ inlineError }}</p>
        </div>
    </DemoToolsLauncher>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type {
    DemoBootstrapQueuedResponse,
    DemoBootstrapRequest,
    FillRemainingAnswersResponse,
    QuestionResponseData,
    SyntheticCampaignFillRequest,
    SyntheticFillQueuedResponse,
} from "~vue/api/generated/reflections";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import DemoToolsLauncher, { type DemoToolsStatus } from "~vue/DemoTools/DemoToolsLauncher.vue";
import { demoToolsErrorMessage } from "~vue/DemoTools/errorMessage";
import { computed, inject, nextTick, type Ref, ref, useTemplateRef, watch } from "vue";

interface DemoAction {
    id: string;
    label: string;
    // Cycles that can't take a synthetic fill (past, or set up but never
    // launched) are still listed so they can be deleted.
    canFill?: boolean;
}

interface Props {
    hubFallback?: boolean;
    cycleActions?: DemoAction[];
    campaignActions?: DemoAction[];
    responseActions?: DemoAction[];
    bootstrapKind?: DemoBootstrapRequest["kind"];
    // Saves the host's unsaved answers. The server fills whatever it has no answer for,
    // so an answer still only on screen would be filled over and then lost.
    beforeFill?: () => Promise<boolean>;
}

type ActionKind = "cycle" | "campaign" | "response";

type SelectedAction = DemoAction & { kind: ActionKind };

const { cycleActions = [], campaignActions = [], responseActions = [], bootstrapKind = undefined, beforeFill = undefined, hubFallback = false } = defineProps<Props>();
const emit = defineEmits<{ filled: [questionResponses: QuestionResponseData[]]; deleted: [cycleId: string] }>();

const kindLabels: Record<ActionKind, string> = {
    cycle: "360 cycle",
    campaign: "Campaign",
    response: "Your assessment",
};
const launcher = useTemplateRef("launcher");
const popoverOpen = ref(false);
const selectedAction = ref<SelectedAction | null>(null);
const subjectCount = ref(5);
const busyActionKey = ref<string | null>(null);
const confirmingDelete = ref(false);
const deleteButton = useTemplateRef("deleteButton");
const confirmDeleteButton = useTemplateRef("confirmDeleteButton");
const inlineError = ref<string | null>(null);

// Gated here rather than in the shared launcher, which also hosts the Perspective demo tools.
const userDetails = inject<Ref<UserDetails>>("user_details");
const isEnabledForUser = computed(() => userDetails?.value.assessment_demo_tools_enabled === true);
const hasActions = computed(() => cycleActions.length > 0 || campaignActions.length > 0 || responseActions.length > 0 || bootstrapKind !== undefined);
const bootstrapLabel = computed(() => (bootstrapKind === "self" ? "self-assessment" : "360"));
const actionButtonText = computed(() => {
    if (selectedAction.value?.kind === "response") {
        return "Fill and skip to the end";
    }
    return selectedAction.value?.kind === "cycle" ? "Complete synthetically" : "Fill synthetically";
});
const busyButtonText = computed(() => (selectedAction.value?.kind === "response" ? "Filling…" : "Queuing…"));
const actionAriaLabel = computed(() => {
    const action = selectedAction.value;
    if (!action) {
        return "";
    }
    switch (action.kind) {
        case "campaign":
            return `Add synthetic participants to ${action.label}`;
        case "response":
            return `Fill the remaining answers on ${action.label} and skip to the end`;
        default:
            return `Fill ${action.label} with synthetic responses`;
    }
});
const isDeleting = computed(() => busyActionKey.value !== null && busyActionKey.value.startsWith("delete:"));
const isSelectionValid = computed(() => selectedAction.value?.kind !== "campaign" || (Number.isInteger(subjectCount.value) && subjectCount.value >= 1 && subjectCount.value <= 20));

// Reopening the launcher should land on the list, not the last assessment.
watch(popoverOpen, (open) => {
    if (!open && busyActionKey.value === null) {
        backToList();
    }
});

function openAction(kind: SelectedAction["kind"], action: DemoAction): void {
    selectedAction.value = { ...action, kind };
    subjectCount.value = 5;
    inlineError.value = null;
    confirmingDelete.value = false;
}

function backToList(): void {
    selectedAction.value = null;
    inlineError.value = null;
    confirmingDelete.value = false;
}

function errorMessage(statusCode: number | null, serverMessage: string, fallback = "Could not queue the synthetic fill."): string {
    switch (statusCode) {
        case 404:
            return "This assessment no longer exists.";
        case 422:
            return "Choose a whole number between 1 and 20.";
        case 409:
        case 410:
            return serverMessage || "This assessment can no longer be filled.";
        default:
            return demoToolsErrorMessage(statusCode, serverMessage, fallback);
    }
}

function finish(status: DemoToolsStatus): void {
    launcher.value?.finish(status);
    backToList();
}

// Both steps swap the focused button out of the DOM, so focus has to follow
// the flow or a keyboard user lands back at the top of the popover.
async function startConfirmingDelete(): Promise<void> {
    confirmingDelete.value = true;
    await nextTick();
    confirmDeleteButton.value?.$el?.focus();
}

async function cancelConfirmingDelete(): Promise<void> {
    confirmingDelete.value = false;
    await nextTick();
    deleteButton.value?.$el?.focus();
}

async function deleteSelectedCycle(): Promise<void> {
    const action = selectedAction.value;
    if (!action || action.kind !== "cycle" || busyActionKey.value !== null) {
        return;
    }

    busyActionKey.value = `delete:${action.id}`;
    inlineError.value = null;
    const { error, statusCode } = await useFetch(`/v1/reflections/demo/cycles/${action.id}`).delete();
    busyActionKey.value = null;

    if (error.value) {
        inlineError.value = errorMessage(statusCode.value, error.value.message, "Could not delete this assessment.");
        return;
    }

    emit("deleted", action.id);
    finish({ theme: "success", text: `Deleted "${action.label}".` });
}

async function runSelectedAction(): Promise<void> {
    const action = selectedAction.value;
    if (!action || busyActionKey.value !== null || !isSelectionValid.value) {
        return;
    }

    busyActionKey.value = `${action.kind}:${action.id}`;
    inlineError.value = null;
    if (action.kind === "response") {
        await fillRemainingAnswers(action);
        return;
    }
    const url = action.kind === "campaign" ? `/v1/reflections/admin/campaigns/${action.id}/fill-synthetic` : `/v1/reflections/cycles/${action.id}/fill-synthetic`;
    const request = action.kind === "campaign" ? ({ num_subjects: subjectCount.value } satisfies SyntheticCampaignFillRequest) : undefined;
    const { data, error, statusCode } = await useFetch(url).post(request).json<SyntheticFillQueuedResponse>();
    busyActionKey.value = null;

    // Errors stay in the panel so the popover keeps the failing assessment in view.
    if (error.value) {
        inlineError.value = errorMessage(statusCode.value, error.value.message);
        return;
    }

    const queuedCount = data.value?.num_subjects;
    finish({
        theme: "success",
        text:
            action.kind === "campaign"
                ? `Queued ${queuedCount ?? subjectCount.value} synthetic participants for "${action.label}".`
                : `Queued synthetic responses for "${action.label}".`,
    });
}

async function bootstrapAssessment(): Promise<void> {
    if (!bootstrapKind || busyActionKey.value !== null) {
        return;
    }

    busyActionKey.value = "bootstrap";
    inlineError.value = null;
    const request = { kind: bootstrapKind } satisfies DemoBootstrapRequest;
    const { error, statusCode } = await useFetch("/v1/reflections/demo/bootstrap").post(request).json<DemoBootstrapQueuedResponse>();
    busyActionKey.value = null;

    if (error.value) {
        inlineError.value = errorMessage(statusCode.value, error.value.message, "Could not create the demo assessment.");
        return;
    }

    finish({ theme: "success", text: `Queued a completed ${bootstrapLabel.value} in your name. Refresh this page in a minute to see it.` });
}

async function fillRemainingAnswers(action: SelectedAction): Promise<void> {
    if (beforeFill && !(await beforeFill())) {
        busyActionKey.value = null;
        inlineError.value = "Couldn't save your current answers, so nothing was filled. Try again.";
        return;
    }
    const { data, error, statusCode } = await useFetch(`/v1/reflections/responses/${action.id}/fill-remaining`).post().json<FillRemainingAnswersResponse>();
    busyActionKey.value = null;

    if (error.value) {
        inlineError.value = errorMessage(statusCode.value, error.value.message);
        return;
    }

    const filled = data.value?.question_responses ?? [];
    emit("filled", filled);
    finish(
        filled.length === 0
            ? { theme: "info", text: "Nothing left to fill — skipped you to the end." }
            : { theme: "success", text: `Filled ${filled.length} ${filled.length === 1 ? "answer" : "answers"} and skipped to the end. Submit when you're ready.` },
    );
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    /* Not `.list-item` — that name is a Tailwind display utility, and the
       utilities layer would override this rule's `display`. */
    .assessment-row {
        @apply flex w-full items-center gap-2 overflow-hidden text-left;
    }

    .back {
        @apply inline-flex size-7 shrink-0 items-center justify-center rounded-full p-0;
    }

    .action {
        @apply inline-flex w-full items-center justify-center gap-2;
    }

    .count-input {
        @apply body w-full rounded-lg border border-control-border bg-white px-3 py-2 text-ink focus-field;
    }
}
</style>
